import React, { useLayoutEffect } from 'react';

import { useIntl } from 'react-intl';

import { NavEmptyState } from '@atlassian/navigation-common/ui/nav-empty-state';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { getMessagesForLocation } from '../../common/utils/get-messages-for-location';
import type { NavErrorBoundaryProps } from '../types';

export function ErrorBoundaryUI({
	disableAnalytics,
	errorFallbackUI,
	errorActionUI,
	location,
	errorMessages,
}: ErrorBoundaryUIProps):
	| string
	| number
	| boolean
	| React.JSX.Element
	| Iterable<React.ReactNode>
	| null
	| undefined {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();

	// Message precedence: errorMessages (consumer-provided) > location-based lookup
	const { header, description, values } = errorMessages
		? { header: errorMessages.header, description: errorMessages.description, values: undefined }
		: getMessagesForLocation(location);

	useLayoutEffect(() => {
		if (!disableAnalytics) {
			fireEvent('ui.errorBoundary.viewed.errorBoundaryUI', {
				location,
				isFallbackUI: !!errorFallbackUI,
			});
		}
	}, [disableAnalytics, errorFallbackUI, fireEvent, location]);

	// If a custom fallback UI is provided, render it directly at risk of consumer
	if (errorFallbackUI) {
		return errorFallbackUI({
			header: intl.formatMessage(header, values),
			description: intl.formatMessage(description, values),
		});
	}

	return (
		<NavEmptyState
			testId="navigation-error-boundary-ui"
			header={intl.formatMessage(header, values)}
			description={intl.formatMessage(description, values)}
			primaryAction={errorActionUI}
		/>
	);
}

type ErrorBoundaryUIProps = Pick<
	NavErrorBoundaryProps,
	'disableAnalytics' | 'errorFallbackUI' | 'errorActionUI' | 'errorMessages'
> & { location: string };
